import React, { useEffect, useState } from "react";
import { User, Camera, Trash2, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { TextInput, showAlert, showErrorAlert } from "../../../../../../../components/ui/index.js";
import { useEditor } from "../../../../../../../App.jsx";

const ProfileUserProfile = ({ setEditorID, setEditor }) => {
    const { stateEditor, setStateEditor } = useEditor();

    const [formData, setFormData] = useState({
        name: "",
        public_username: "",
        foto_perfil: "",
    });

    const [fotoPerfilBase64, setFotoPerfilBase64] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const closeEditor = () => {
        setEditor?.(false);
        setStateEditor({
            ...stateEditor,
            editor: false,
            editorType: null,
            editorID: null,
        });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevState) => ({
            ...prevState,
            [name]: value,
        }));

        // Limpiar errores cuando el usuario empiece a escribir
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: "" }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = "El nombre es requerido";
        } else if (formData.name.length < 2) {
            newErrors.name = "El nombre debe tener al menos 2 caracteres";
        }

        if (!formData.public_username.trim()) {
            newErrors.public_username = "El nombre de usuario es requerido";
        } else if (formData.public_username.length < 3) {
            newErrors.public_username = "El nombre de usuario debe tener al menos 3 caracteres";
        } else if (!/^[a-zA-Z0-9_]+$/.test(formData.public_username)) {
            newErrors.public_username = "Solo se permiten letras, números y guiones bajos";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setIsLoading(true);

        try {
            await axios.put("/data_profile_user/", { ...formData, foto_perfil: fotoPerfilBase64 });

            const result = await showAlert({
                title: "Perfil actualizado",
                text: "¿Quieres volver a tu perfil?",
                icon: "success",
                showCancelButton: true,
                confirmButtonText: "Volver al perfil",
                cancelButtonText: "Seguir editando",
            });
            if (result.isConfirmed) {
                closeEditor();
            }
        } catch (error) {
            console.error("Error updating profile:", error);
            showErrorAlert({ text: "No se pudo actualizar el perfil. Inténtalo de nuevo." });
        } finally {
            setIsLoading(false);
        }
    };

    // Cargar datos del usuario
    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const response = await axios.get("data_profile_user/");
                setFormData(response.data);
                setFotoPerfilBase64(response.data.foto_perfil);
            } catch (error) {
                console.error("Error fetching user data:", error);
            }
        };

        fetchUserData();
    }, []);

    return (
        <div className="glassCard rounded-2xl p-6 w-full">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-base-300">
                <div className="p-2 bg-primary/20 rounded-lg">
                    <User className="h-6 w-6 text-primary" />
                </div>
                <div>
                    <h2 className="text-xl font-bold text-base-content">Información del perfil</h2>
                    <p className="text-base-content/70 text-sm">Actualiza tu información personal y foto de perfil</p>
                </div>
            </div>

            <form onSubmit={handleSubmit}>
                {/* Sección de imagen de perfil */}
                <div className="mb-8">
                    <h3 className="text-lg font-semibold text-base-content mb-4">Foto de perfil</h3>
                    <div className="flex items-center gap-6">
                        <ImageProfile
                            fotoPerfilBase64={fotoPerfilBase64}
                            setFotoPerfilBase64={setFotoPerfilBase64}
                            name={formData.name}
                        />
                        <div className="flex-1">
                            <p className="text-sm text-base-content/70 mb-2">
                                Sube una imagen de perfil para personalizar tu cuenta
                            </p>
                            <p className="text-xs text-base-content/50">
                                Formatos recomendados: JPG, PNG. Tamaño máximo: 2MB
                            </p>
                        </div>
                    </div>
                </div>

                {/* Campos del formulario */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    {/* Nombre de usuario público */}
                    <div className="form-control gap-1">
                        <label className="label py-0" htmlFor="public_username">
                            <span className="label-text text-sm font-semibold">Nombre de usuario público</span>
                        </label>
                        <TextInput
                            type="text"
                            id="public_username"
                            name="public_username"
                            value={formData.public_username}
                            onChange={handleChange}
                            variant={errors.public_username ? "error" : undefined}
                            placeholder="nombre_usuario"
                        />
                        {errors.public_username && (
                            <label className="label py-0">
                                <span className="label-text-alt text-error">{errors.public_username}</span>
                            </label>
                        )}
                        <label className="label py-0">
                            <span className="label-text-alt text-base-content/50">
                                Este será tu nombre público visible para otros usuarios
                            </span>
                        </label>
                    </div>

                    {/* Nombre completo */}
                    <div className="form-control gap-1">
                        <label className="label py-0" htmlFor="name">
                            <span className="label-text text-sm font-semibold">Nombre completo</span>
                        </label>
                        <TextInput
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            variant={errors.name ? "error" : undefined}
                            placeholder="Tu nombre completo"
                        />
                        {errors.name && (
                            <label className="label py-0">
                                <span className="label-text-alt text-error">{errors.name}</span>
                            </label>
                        )}
                        <label className="label py-0">
                            <span className="label-text-alt text-base-content/50">
                                Nombre por el cual te contactaremos en nuestras comunicaciones
                            </span>
                        </label>
                    </div>
                </div>

                {/* Acciones */}
                <div className="pt-6 border-t border-base-300 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2 text-sm text-base-content/70">
                        <CheckCircle2 size={16} className="flex-shrink-0" />
                        <span>Tus cambios se guardarán de forma segura</span>
                    </div>
                    <button
                        type="submit"
                        disabled={isLoading}
                        className={`btn btn-primary btn-sm ${isLoading ? "loading" : ""}`}
                    >
                        {isLoading ? "Guardando..." : "Guardar cambios"}
                    </button>
                </div>
            </form>
        </div>
    );
};

// Componente para subir imagen
const ImgUpload = ({ onChange, fotoPerfilBase64, name }) => {
    return (
        <div className="relative group">
            <div className="w-32 h-32 rounded-2xl bg-base-200 border-2 border-base-300 overflow-hidden">
                <label htmlFor="photo-upload" className="relative w-full h-full cursor-pointer block">
                    <div className="relative w-full h-full">
                        {fotoPerfilBase64 ? (
                            <img
                                className="w-full h-full object-cover"
                                src={fotoPerfilBase64}
                                alt="Foto de perfil"
                            />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center bg-primary/10">
                                <span className="text-4xl font-bold text-primary">
                                    {name?.charAt(0)?.toUpperCase() || "U"}
                                </span>
                            </div>
                        )}
                        <div className="absolute inset-0 bg-neutral/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <div className="text-center">
                                <Camera className="h-8 w-8 text-neutral-content mx-auto mb-2" />
                                <span className="text-neutral-content text-sm font-medium">Cambiar foto</span>
                            </div>
                        </div>
                    </div>
                    <input id="photo-upload" type="file" onChange={onChange} className="hidden" accept="image/*" />
                </label>
            </div>
        </div>
    );
};

// Componente principal de imagen
const ImageProfile = ({ fotoPerfilBase64, setFotoPerfilBase64, name }) => {
    const photoUpload = (e) => {
        e.preventDefault();
        const reader = new FileReader();
        const file = e.target.files[0];

        if (file) {
            if (file.size > 2 * 1024 * 1024) {
                showErrorAlert({
                    title: "Imagen demasiado grande",
                    text: "El archivo supera los 2MB. Por favor, selecciona una imagen más pequeña.",
                });
                return;
            }

            reader.onloadend = () => {
                setFotoPerfilBase64(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleDelete = (e) => {
        e.preventDefault();
        setFotoPerfilBase64("");
    };

    return (
        <div className="flex flex-col items-center gap-4">
            <ImgUpload
                onChange={photoUpload}
                fotoPerfilBase64={fotoPerfilBase64}
                name={name}
            />
            {fotoPerfilBase64 && (
                <button
                    onClick={handleDelete}
                    className="btn btn-outline btn-error btn-sm gap-2"
                >
                    <Trash2 size={14} />
                    Eliminar imagen
                </button>
            )}
        </div>
    );
};

export default ProfileUserProfile;
